<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-truck-fast-outline me-2"></i>{{ $t('page.referrals_in', 'Referrals') }}</h4>
        </div>
        <div class="d-flex gap-2 flex-wrap">
          <button class="hms-btn hms-btn-primary" @click="openCreate">
            <i class="mdi mdi-plus me-1"></i>New Referral
          </button>
          <button class="hms-btn hms-btn-blue" @click="loadAll" :disabled="loading">
            <i class="mdi mdi-refresh" :class="{ 'mdi-spin': loading }"></i>
          </button>
        </div>
      </div>

      <!-- Stats -->
      <div class="hms-stat-row">
        <div class="hms-stat" @click="filterStatus='pending'; direction='inbound'" :class="{ selected: direction==='inbound' && filterStatus==='pending' }">
          <div class="hms-stat-num hms-stat-warning">{{ stats.inbound_pending || 0 }}</div>
          <div class="hms-stat-lbl">Inbound Pending</div>
        </div>
        <div class="hms-stat" @click="filterStatus='pending'; filterPriority='urgent'; direction='inbound'" :class="{ selected: filterPriority==='urgent' }">
          <div class="hms-stat-num hms-stat-danger">{{ stats.inbound_urgent || 0 }}</div>
          <div class="hms-stat-lbl">Urgent / Emergency</div>
        </div>
        <div class="hms-stat">
          <div class="hms-stat-num hms-stat-success">{{ stats.accepted_today || 0 }}</div>
          <div class="hms-stat-lbl">Accepted Today</div>
        </div>
        <div class="hms-stat" @click="direction='outbound'" :class="{ selected: direction==='outbound' }">
          <div class="hms-stat-num hms-stat-info">{{ stats.outbound_pending || 0 }}</div>
          <div class="hms-stat-lbl">Outbound Pending</div>
        </div>
        <div class="hms-stat">
          <div class="hms-stat-num">{{ stats.total || 0 }}</div>
          <div class="hms-stat-lbl">Total</div>
        </div>
      </div>

      <!-- Tabs: direction -->
      <div class="hms-tabs mb-3">
        <button class="hms-tab" :class="{ active: direction==='inbound' }"  @click="direction='inbound';  loadAll()"><i class="mdi mdi-tray-arrow-down me-1"></i>Inbound</button>
        <button class="hms-tab" :class="{ active: direction==='outbound' }" @click="direction='outbound'; loadAll()"><i class="mdi mdi-tray-arrow-up me-1"></i>Outbound</button>
        <button class="hms-tab" :class="{ active: direction==='all' }"      @click="direction='all';      loadAll()"><i class="mdi mdi-swap-horizontal me-1"></i>All</button>
      </div>

      <div v-if="loading && !rows.length" class="hms-spinner"><div class="spinner-border"></div></div>

      <div v-else class="hms-card">
        <div class="d-flex flex-wrap gap-2 mb-3 align-items-center">
          <div class="hms-search-wrap">
            <i class="mdi mdi-magnify"></i>
            <input class="hms-search" v-model="search" placeholder="Search referral #, patient, reason..." />
          </div>
          <select class="hms-input hms-filter" v-model="filterStatus">
            <option value="">All status</option>
            <option value="pending">Pending</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select class="hms-input hms-filter" v-model="filterPriority">
            <option value="">All priority</option>
            <option value="emergency">Emergency</option>
            <option value="urgent">Urgent</option>
            <option value="routine">Routine</option>
          </select>
          <span class="hms-muted ms-auto">{{ filteredRows.length }} of {{ rows.length }}</span>
        </div>

        <div v-if="filteredRows.length" class="hms-ref-list">
          <div v-for="r in filteredRows" :key="r.referral_id"
               class="hms-ref" :class="['pri-' + r.priority, { dim: r.status !== 'pending' }]"
               @click="openDetail(r)">
            <div class="hms-ref-head">
              <span class="hms-mono">{{ r.referral_number }}</span>
              <span class="hms-badge" :class="priorityBadge(r.priority)">{{ r.priority }}</span>
              <span class="hms-badge" :class="statusBadge(r.status)">{{ r.status }}</span>
              <span class="hms-muted-sm ms-auto">{{ relativeTime(r.referral_date) }}</span>
            </div>
            <div class="hms-ref-row">
              <div>
                <div class="hms-ref-arrow">
                  <span class="hms-branch">{{ r.from_branch_name || 'Unknown' }} <span class="hms-muted-sm">L{{ r.from_level || '?' }}</span></span>
                  <i class="mdi mdi-arrow-right hms-arrow-icon"></i>
                  <span class="hms-branch">{{ r.to_branch_name || 'Unknown' }} <span class="hms-muted-sm">L{{ r.to_level || '?' }}</span></span>
                </div>
                <div class="hms-ref-patient mt-1">
                  <strong>{{ r.first_name }} {{ r.last_name }}</strong>
                  <span class="hms-muted-sm">{{ r.patient_no }} · {{ ageStr(r.dob) }} · {{ r.gender }}</span>
                </div>
              </div>
            </div>
            <div class="hms-ref-reason">"{{ truncate(r.reason, 140) }}"</div>
            <div class="hms-ref-foot">
              <span class="hms-muted-sm" v-if="r.referring_doctor_name"><i class="mdi mdi-doctor"></i> Referring: {{ r.referring_doctor_name }}</span>
              <span class="hms-muted-sm" v-if="r.transport_arranged == 1"><i class="mdi mdi-ambulance"></i> Transport arranged</span>
              <span class="hms-muted-sm" v-if="r.allergies"><i class="mdi mdi-alert-octagon text-danger"></i> Allergies</span>
            </div>
          </div>
        </div>
        <div v-else class="hms-empty"><i class="mdi mdi-tray-remove"></i>{{ direction === 'inbound' ? 'No incoming referrals.' : (direction === 'outbound' ? 'No outgoing referrals.' : 'No referrals.') }}</div>
      </div>

      <!-- DETAIL DRAWER -->
      <div class="hms-modal-overlay" v-if="detail" @click.self="detail=null">
        <div class="hms-modal">
          <div class="hms-modal-title">
            <span>
              <i class="mdi mdi-truck-fast-outline me-1"></i>{{ detail.Referral.referral_number }}
              <span class="hms-badge ms-2" :class="priorityBadge(detail.Referral.priority)">{{ detail.Referral.priority }}</span>
              <span class="hms-badge ms-1" :class="statusBadge(detail.Referral.status)">{{ detail.Referral.status }}</span>
            </span>
            <button class="hms-modal-close" @click="detail=null">&times;</button>
          </div>

          <div class="hms-info-banner mb-3">
            <span class="hms-branch">{{ detail.Referral.from_branch_name }} <span class="hms-muted-sm">L{{ detail.Referral.from_level || '?' }}</span></span>
            <i class="mdi mdi-arrow-right mx-2"></i>
            <span class="hms-branch">{{ detail.Referral.to_branch_name }} <span class="hms-muted-sm">L{{ detail.Referral.to_level || '?' }}</span></span>
            <div class="hms-muted-sm mt-1">Sent {{ formatDateTime(detail.Referral.referral_date) }}</div>
          </div>

          <div class="hms-section-label">PATIENT</div>
          <div class="hms-kv mb-3">
            <div><span class="hms-kv-key">Name</span><span><strong>{{ detail.Referral.first_name }} {{ detail.Referral.last_name }}</strong></span></div>
            <div><span class="hms-kv-key">Patient #</span><span class="hms-mono">{{ detail.Referral.patient_no }}</span></div>
            <div><span class="hms-kv-key">DOB / Age</span><span>{{ formatDate(detail.Referral.dob) }} · {{ ageStr(detail.Referral.dob) }}</span></div>
            <div><span class="hms-kv-key">Sex / BG</span><span>{{ detail.Referral.gender }} · {{ detail.Referral.blood_group || '—' }}</span></div>
            <div v-if="detail.Referral.allergies"><span class="hms-kv-key">Allergies</span><span class="text-danger"><strong>{{ detail.Referral.allergies }}</strong></span></div>
            <div v-if="detail.Referral.chronic_conditions"><span class="hms-kv-key">Chronic</span><span>{{ detail.Referral.chronic_conditions }}</span></div>
            <div><span class="hms-kv-key">Phone</span><span>{{ detail.Referral.phone || '—' }}</span></div>
            <div v-if="detail.Referral.next_of_kin_name"><span class="hms-kv-key">Next of Kin</span><span>{{ detail.Referral.next_of_kin_name }} · {{ detail.Referral.next_of_kin_phone || '—' }}</span></div>
            <div v-if="detail.Referral.insurance_provider"><span class="hms-kv-key">Insurance</span><span>{{ detail.Referral.insurance_provider }} · <span class="hms-mono">{{ detail.Referral.insurance_number }}</span></span></div>
          </div>

          <div class="hms-section-label">REFERRAL</div>
          <div class="hms-kv mb-3">
            <div><span class="hms-kv-key">Reason</span><span><strong>{{ detail.Referral.reason }}</strong></span></div>
            <div v-if="detail.Referral.clinical_summary"><span class="hms-kv-key">Clinical Summary</span><span class="hms-pre">{{ detail.Referral.clinical_summary }}</span></div>
            <div><span class="hms-kv-key">Referring Doctor</span><span>{{ detail.Referral.referring_doctor_name || '—' }} <span class="hms-muted-sm" v-if="detail.Referral.referring_doctor_phone">· {{ detail.Referral.referring_doctor_phone }}</span></span></div>
            <div v-if="detail.Referral.from_phone"><span class="hms-kv-key">Referring Hospital</span><span>{{ detail.Referral.from_phone }}</span></div>
            <div><span class="hms-kv-key">Transport</span><span>{{ detail.Referral.transport_arranged == 1 ? 'Arranged' : 'Not arranged' }}{{ detail.Referral.transport_details ? ' · ' + detail.Referral.transport_details : '' }}</span></div>
            <div v-if="detail.Referral.accepted_date"><span class="hms-kv-key">Accepted</span><span>{{ formatDateTime(detail.Referral.accepted_date) }} {{ detail.Referral.receiving_doctor_name ? '· by ' + detail.Referral.receiving_doctor_name : '' }}</span></div>
            <div v-if="detail.Referral.rejection_reason"><span class="hms-kv-key">Rejection Reason</span><span class="text-danger">{{ detail.Referral.rejection_reason }}</span></div>
          </div>

          <div v-if="detail.Vitals" class="mb-3">
            <div class="hms-section-label">LAST VITALS ({{ relativeTime(detail.Vitals.recorded_at) }})</div>
            <div class="hms-vitals-strip">
              <div class="hms-vital-cell"><div class="hms-vital-num">{{ detail.Vitals.bp_systolic }}/{{ detail.Vitals.bp_diastolic }}</div><div class="hms-vital-lbl">BP</div></div>
              <div class="hms-vital-cell"><div class="hms-vital-num">{{ detail.Vitals.pulse }}</div><div class="hms-vital-lbl">HR</div></div>
              <div class="hms-vital-cell"><div class="hms-vital-num">{{ detail.Vitals.temperature }}</div><div class="hms-vital-lbl">°C</div></div>
              <div class="hms-vital-cell"><div class="hms-vital-num">{{ detail.Vitals.oxygen_saturation }}</div><div class="hms-vital-lbl">SpO₂</div></div>
              <div class="hms-vital-cell" :class="news2Class(detail.Vitals.news2_score)"><div class="hms-vital-num">{{ detail.Vitals.news2_score != null ? detail.Vitals.news2_score : '—' }}</div><div class="hms-vital-lbl">NEWS2</div></div>
            </div>
          </div>

          <div v-if="detail.Diagnoses && detail.Diagnoses.length" class="mb-3">
            <div class="hms-section-label">RECENT DIAGNOSES</div>
            <div v-for="d in detail.Diagnoses" :key="d.diagnosis_date + d.diagnosis_name" class="hms-diag-row">
              <span class="hms-mono">{{ d.icd_code || '—' }}</span>
              <span>{{ d.diagnosis_name }}</span>
              <span class="hms-muted-sm ms-auto">{{ formatDate(d.diagnosis_date) }}</span>
            </div>
          </div>

          <div v-if="detail.Rx && detail.Rx.length" class="mb-3">
            <div class="hms-section-label">RECENT MEDICATIONS</div>
            <div v-for="r in detail.Rx" :key="r.prescription_number" class="hms-diag-row">
              <span class="hms-mono">{{ r.prescription_number }}</span>
              <span>{{ r.drugs || '—' }}</span>
              <span class="hms-muted-sm ms-auto">{{ formatDate(r.prescription_date) }}</span>
            </div>
          </div>

          <div class="d-flex gap-2 flex-wrap mt-3">
            <button class="hms-btn hms-btn-blue" @click="openChart">
              <i class="mdi mdi-folder-account-outline me-1"></i>Open Full Chart
            </button>

            <!-- Inbound: pending → accept / reject -->
            <template v-if="isInbound(detail.Referral) && detail.Referral.status === 'pending'">
              <button class="hms-btn hms-btn-danger ms-auto" @click="openReject">
                <i class="mdi mdi-close-circle-outline me-1"></i>Reject
              </button>
              <button class="hms-btn hms-btn-success" @click="acceptCurrent" :disabled="processing">
                <i class="mdi mdi-check-circle-outline me-1"></i>Accept
              </button>
            </template>

            <!-- Inbound accepted → mark complete -->
            <template v-else-if="isInbound(detail.Referral) && detail.Referral.status === 'accepted'">
              <button class="hms-btn hms-btn-primary ms-auto" @click="completeCurrent" :disabled="processing">
                <i class="mdi mdi-check-decagram me-1"></i>Mark Complete
              </button>
            </template>

            <!-- Outbound pending → cancel -->
            <template v-else-if="!isInbound(detail.Referral) && detail.Referral.status === 'pending'">
              <button class="hms-btn hms-btn-amber ms-auto" @click="cancelCurrent" :disabled="processing">
                <i class="mdi mdi-cancel me-1"></i>Cancel Referral
              </button>
            </template>
          </div>
        </div>
      </div>

      <!-- REJECT MODAL -->
      <div class="hms-modal-overlay" v-if="showReject" @click.self="showReject=false">
        <div class="hms-modal hms-modal-sm">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-close-circle-outline me-1"></i>Reject Referral</span>
            <button class="hms-modal-close" @click="showReject=false">&times;</button>
          </div>
          <div v-if="rejectError" class="hms-alert hms-alert-danger"><i class="mdi mdi-alert-circle me-1"></i>{{ rejectError }}</div>
          <div class="hms-info-banner mb-3">
            <i class="mdi mdi-information-outline me-1"></i>
            The referring facility will see this reason. Be specific so they can route the patient elsewhere.
          </div>
          <label class="hms-label">Reason *</label>
          <textarea class="hms-input mb-3" rows="4" v-model="rejectReason" placeholder="e.g. ICU full, no neurosurgery on call, unable to accept until tomorrow 09:00..."></textarea>
          <div class="d-flex gap-2">
            <button class="hms-btn hms-btn-amber flex-fill" @click="showReject=false">Back</button>
            <button class="hms-btn hms-btn-danger flex-fill" @click="rejectCurrent" :disabled="processing || !rejectReason">Confirm Rejection</button>
          </div>
        </div>
      </div>

      <!-- CREATE MODAL -->
      <div class="hms-modal-overlay" v-if="showCreate" @click.self="showCreate=false">
        <div class="hms-modal">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-plus-circle me-1"></i>New Referral</span>
            <button class="hms-modal-close" @click="showCreate=false">&times;</button>
          </div>
          <div v-if="createError" class="hms-alert hms-alert-danger"><i class="mdi mdi-alert-circle me-1"></i>{{ createError }}</div>

          <div class="row g-3 mb-3">
            <div class="col-md-12"><label class="hms-label">Patient *</label>
              <select class="hms-input" v-model="newRef.patient_id">
                <option value="">— Select patient —</option>
                <option v-for="p in patients" :key="p.patient_id" :value="p.patient_id">
                  {{ p.first_name }} {{ p.last_name }} · {{ p.patient_no }}
                </option>
              </select>
            </div>
            <div class="col-md-7"><label class="hms-label">Refer To *</label>
              <select class="hms-input" v-model="newRef.to_branch_id">
                <option value="">— Select destination —</option>
                <option v-for="b in destinationBranches" :key="b.branch_id" :value="b.branch_id">
                  L{{ b.facility_level }} · {{ b.branch_name }}
                </option>
              </select>
            </div>
            <div class="col-md-5"><label class="hms-label">Priority</label>
              <select class="hms-input" v-model="newRef.priority">
                <option value="routine">Routine</option>
                <option value="urgent">Urgent</option>
                <option value="emergency">Emergency</option>
              </select>
            </div>
            <div class="col-12"><label class="hms-label">Reason *</label>
              <input class="hms-input" v-model="newRef.reason" placeholder="One-line reason for referral" />
            </div>
            <div class="col-12"><label class="hms-label">Clinical Summary</label>
              <textarea class="hms-input" rows="4" v-model="newRef.clinical_summary" placeholder="Presenting complaint, key findings, investigations done, treatments given, current status..."></textarea>
            </div>
            <div class="col-md-4 d-flex align-items-end">
              <label class="hms-toggle">
                <input type="checkbox" :checked="newRef.transport_arranged == 1" @change="newRef.transport_arranged = $event.target.checked ? 1 : 0" />
                <span>Transport arranged</span>
              </label>
            </div>
            <div class="col-md-8" v-if="newRef.transport_arranged == 1"><label class="hms-label">Transport Details</label>
              <input class="hms-input" v-model="newRef.transport_details" placeholder="Mode, ETA, escort..." />
            </div>
          </div>

          <button class="hms-btn hms-btn-primary w-100" @click="submitCreate" :disabled="processing">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
            {{ processing ? 'Sending...' : 'Send Referral' }}
          </button>
        </div>
      </div>

      <transition name="hms-toast">
        <div v-if="toastMsg" class="hms-toast" :class="{ 'hms-toast-error': toastError }">
          <i class="mdi me-2" :class="toastError ? 'mdi-alert-circle' : 'mdi-check-circle'"></i>{{ toastMsg }}
        </div>
      </transition>
    </div>
  </div>
</template>

<style>
:root, html[data-theme="dark"] {
  --hms-h:#f0f0f0;--hms-s:#a0a0a0;--hms-m:#808080;
  --hms-bg1:#141414;--hms-bg2:#0a0a0a;--hms-bg3:#1e1e1e;--hms-bg4:#0f0f0f;
  --hms-br:#2a2a2a;--hms-input-br:#333333;
  --hms-primary:#1a7fe8;--hms-primary-lt:rgba(26,127,232,0.12);
  --hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] {
  --hms-h:#1a1a1a;--hms-s:#555555;--hms-m:#777777;
  --hms-bg1:#ffffff;--hms-bg2:#f4f6f9;--hms-bg3:#eef1f5;--hms-bg4:#ffffff;
  --hms-br:#dee2e8;--hms-input-br:#ced4da;
  --hms-primary:#0047AB;--hms-primary-lt:rgba(0,71,171,0.08);
  --hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] body, html[data-theme="dark"] body { background: var(--hms-bg2) !important; color: var(--hms-h) !important; }
</style>

<style scoped>
.hms-page { min-height: 100vh; background: var(--hms-bg2); padding-top: 70px; color: var(--hms-h); }
.hms-page-body { padding-top: 20px; padding-bottom: 40px; }
.hms-page-header { flex-wrap: wrap; gap: 12px; }
.hms-back { color: var(--hms-s); font-size: 22px; padding: 4px; }
.hms-back:hover { color: var(--hms-primary); }
.hms-page-title { color: var(--hms-h); font-size: 20px; font-weight: 700; }

.hms-muted { color: var(--hms-s); font-size: 13px; }
.hms-muted-sm { color: var(--hms-s); font-size: 12px; }
.hms-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; }
.text-danger { color: var(--hms-danger); }

.hms-stat-row { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 20px; }
.hms-stat { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 12px; padding: 14px 18px; flex: 1; min-width: 130px; cursor: pointer; transition: all .15s; }
.hms-stat:hover { transform: translateY(-2px); border-color: var(--hms-primary); }
.hms-stat.selected { border-color: var(--hms-primary); background: var(--hms-primary-lt); }
.hms-stat-num { font-size: 22px; font-weight: 800; color: var(--hms-h); line-height: 1.1; }
.hms-stat-lbl { font-size: 12px; color: var(--hms-s); margin-top: 4px; }
.hms-stat-warning { color: var(--hms-warning); }
.hms-stat-danger  { color: var(--hms-danger); }
.hms-stat-success { color: var(--hms-success); }
.hms-stat-info    { color: var(--hms-info); }

.hms-tabs { display: flex; flex-wrap: wrap; gap: 6px; }
.hms-tab { background: var(--hms-bg1); border: 1px solid var(--hms-br); color: var(--hms-m); border-radius: 50px; padding: 7px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; }
.hms-tab:hover, .hms-tab.active { background: var(--hms-primary-lt); border-color: var(--hms-primary); color: var(--hms-primary); }

.hms-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 14px; padding: 18px; }
.hms-spinner { display: flex; justify-content: center; padding: 40px; }
.hms-spinner .spinner-border { color: var(--hms-primary); }
.hms-empty { text-align: center; padding: 40px 20px; color: var(--hms-s); }
.hms-empty i { font-size: 48px; color: var(--hms-br); display: block; margin-bottom: 12px; }

.hms-search-wrap { position: relative; flex: 1; min-width: 220px; max-width: 340px; }
.hms-search-wrap i { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--hms-s); }
.hms-search { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px 8px 36px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-search:focus { border-color: var(--hms-primary); }
.hms-input { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-input:focus { border-color: var(--hms-primary); }
.hms-filter { width: auto; padding: 7px 12px; font-size: 13px; }
.hms-label { color: var(--hms-m); font-size: 12px; font-weight: 600; margin-bottom: 4px; display: block; }
.hms-toggle { display: flex; align-items: center; gap: 8px; cursor: pointer; color: var(--hms-h); font-size: 13px; }
.hms-toggle input { width: 18px; height: 18px; accent-color: var(--hms-primary); }

/* Referral cards */
.hms-ref-list { display: flex; flex-direction: column; gap: 10px; }
.hms-ref { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-left: 6px solid var(--hms-br); border-radius: 12px; padding: 14px 16px; cursor: pointer; transition: all .15s; }
.hms-ref:hover { transform: translateX(2px); border-color: var(--hms-primary); }
.hms-ref.dim { opacity: 0.6; }
.hms-ref.pri-emergency { border-left-color: var(--hms-danger); }
.hms-ref.pri-urgent    { border-left-color: var(--hms-warning); }
.hms-ref.pri-routine   { border-left-color: var(--hms-info); }

.hms-ref-head   { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 8px; }
.hms-ref-row    { display: flex; gap: 12px; align-items: flex-start; flex-wrap: wrap; }
.hms-ref-arrow  { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.hms-branch     { color: var(--hms-h); font-weight: 600; font-size: 13px; }
.hms-arrow-icon { color: var(--hms-primary); font-size: 18px; }
.hms-ref-patient { color: var(--hms-h); font-size: 14px; }
.hms-ref-reason { color: var(--hms-s); font-style: italic; font-size: 13px; margin: 8px 0; }
.hms-ref-foot   { display: flex; gap: 16px; flex-wrap: wrap; padding-top: 8px; border-top: 1px dashed var(--hms-br); }

.hms-badge { padding: 2px 10px; border-radius: 50px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; display: inline-block; }
.hms-badge-success { background: rgba(40,167,69,.15); color: var(--hms-success); }
.hms-badge-warning { background: rgba(255,193,7,.15); color: #ca8a04; }
.hms-badge-danger  { background: rgba(220,53,69,.15); color: var(--hms-danger); }
.hms-badge-info    { background: var(--hms-primary-lt); color: var(--hms-primary); }
.hms-badge-neutral { background: var(--hms-bg3); color: var(--hms-m); }
.hms-badge-purple  { background: rgba(155,89,182,.15); color: #9b59b6; }

.hms-btn { border: none; border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; justify-content: center; }
.hms-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.hms-btn-primary { background: var(--hms-primary); color: #fff; }
.hms-btn-primary:hover:not(:disabled) { filter: brightness(1.1); }
.hms-btn-blue { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); }
.hms-btn-blue:hover:not(:disabled) { background: var(--hms-primary); color: #fff; }
.hms-btn-success { background: var(--hms-success); color: #fff; }
.hms-btn-success:hover:not(:disabled) { filter: brightness(1.1); }
.hms-btn-danger { background: rgba(220,53,69,.15); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }
.hms-btn-danger:hover:not(:disabled) { background: var(--hms-danger); color: #fff; }
.hms-btn-amber { background: rgba(245,158,11,.15); color: #f59e0b; border: 1px solid rgba(245,158,11,.3); }

/* Modal */
.hms-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.6); z-index: 200; display: flex; align-items: flex-start; justify-content: center; padding: 40px 20px; overflow-y: auto; }
.hms-modal { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 16px; padding: 24px; width: 100%; max-width: 760px; max-height: calc(100vh - 80px); overflow-y: auto; box-shadow: 0 24px 60px rgba(0,0,0,.3); }
.hms-modal-sm { max-width: 480px; }
.hms-modal-title { color: var(--hms-h); font-size: 18px; font-weight: 700; margin-bottom: 18px; display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.hms-modal-close { color: var(--hms-s); cursor: pointer; font-size: 24px; line-height: 1; background: none; border: none; padding: 0 4px; }

.hms-section-label { font-size: 11px; font-weight: 700; color: var(--hms-s); letter-spacing: 1.5px; margin-bottom: 8px; }
.hms-kv { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; padding: 12px 16px; }
.hms-kv > div { display: flex; padding: 6px 0; border-bottom: 1px dashed var(--hms-br); font-size: 13px; color: var(--hms-h); gap: 8px; }
.hms-kv > div:last-child { border-bottom: none; }
.hms-kv-key { color: var(--hms-s); flex: 0 0 150px; font-weight: 600; }
.hms-pre { white-space: pre-wrap; }

.hms-vitals-strip { display: flex; gap: 8px; flex-wrap: wrap; }
.hms-vital-cell { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 8px; padding: 8px 14px; text-align: center; min-width: 70px; }
.hms-vital-num { color: var(--hms-h); font-size: 16px; font-weight: 800; }
.hms-vital-lbl { color: var(--hms-s); font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 2px; }
.hms-vital-cell.news2-low  { border-color: var(--hms-success); }
.hms-vital-cell.news2-med  { border-color: var(--hms-warning); }
.hms-vital-cell.news2-high { border-color: var(--hms-danger); background: rgba(220,53,69,.08); }

.hms-diag-row { display: flex; gap: 10px; align-items: center; padding: 6px 12px; background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 8px; font-size: 13px; flex-wrap: wrap; margin-bottom: 4px; }

.hms-info-banner { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); border-radius: 8px; padding: 10px 14px; font-size: 13px; }

.hms-alert { padding: 10px 14px; border-radius: 8px; font-size: 13px; margin-bottom: 12px; }
.hms-alert-danger { background: rgba(220,53,69,.1); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }

.hms-toast { position: fixed; bottom: 24px; right: 24px; background: var(--hms-bg1); border: 1px solid var(--hms-success); color: var(--hms-h); padding: 12px 18px; border-radius: 10px; box-shadow: 0 12px 32px rgba(40,167,69,.2); z-index: 1000; font-size: 13px; display: flex; align-items: center; }
.hms-toast i { color: var(--hms-success); }
.hms-toast.hms-toast-error { border-color: var(--hms-danger); box-shadow: 0 12px 32px rgba(220,53,69,.2); }
.hms-toast.hms-toast-error i { color: var(--hms-danger); }
.hms-toast-enter-active, .hms-toast-leave-active { transition: all .25s ease; }
.hms-toast-enter-from, .hms-toast-leave-to { opacity: 0; transform: translateY(20px); }
</style>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "ReferralInbox",
  components: { Navigation },
  data() {
    return {
      loading: true, processing: false,
      direction: 'inbound',          // inbound | outbound | all
      filterStatus: '', filterPriority: '', search: '',

      rows: [], stats: {},

      detail: null,                  // { Referral, Diagnoses, Vitals, Rx }

      showReject: false, rejectError: '', rejectReason: '',

      showCreate: false, createError: '',
      newRef: { patient_id:'', to_branch_id:'', reason:'', clinical_summary:'', priority:'routine', transport_arranged:0, transport_details:'' },
      patients: [], branches: [],
      myBranchId: null,

      toastMsg: '', toastError: false, toastTimer: null
    };
  },
  computed: {
    filteredRows() {
      var list = this.rows;
      if (this.filterStatus)   list = list.filter(r => r.status   === this.filterStatus);
      if (this.filterPriority) list = list.filter(r => r.priority === this.filterPriority);
      var q = (this.search || '').trim().toLowerCase();
      if (q) {
        list = list.filter(r => ((r.referral_number||'') + ' ' + (r.first_name||'') + ' ' + (r.last_name||'') + ' ' + (r.patient_no||'') + ' ' + (r.reason||'')).toLowerCase().includes(q));
      }
      return list;
    },
    destinationBranches() {
      return (this.branches || []).filter(b => String(b.branch_id) !== String(this.myBranchId));
    }
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) { this.$router.push("/login"); return; }
    this.applyTheme();
    this.myBranchId = sessionStorage.getItem("branchId");
    this.loadAll();
    this.loadLookups();
  },
  beforeUnmount() { if (this.toastTimer) clearTimeout(this.toastTimer); },
  methods: {
    applyTheme() {
      var t = localStorage.getItem("hms_theme") || document.documentElement.getAttribute("data-theme") || "light";
      document.documentElement.setAttribute("data-theme", t);
    },
    showToast(msg, isError) {
      this.toastMsg = msg; this.toastError = !!isError;
      if (this.toastTimer) clearTimeout(this.toastTimer);
      var self = this;
      this.toastTimer = setTimeout(() => { self.toastMsg = ""; }, 2400);
    },

    isInbound(r) { return String(r.to_branch_id) === String(this.myBranchId); },

    async loadAll() {
      this.loading = true;
      try {
        var payload = { function: "getReferrals", direction: this.direction };
        if (this.filterStatus)   payload.status   = this.filterStatus;
        if (this.filterPriority) payload.priority = this.filterPriority;
        var r = await executeJson(payload, hms);
        if (r.data && r.data.success) {
          this.rows  = (r.data.data && r.data.data.Referrals) || [];
          this.stats = (r.data.data && r.data.data.Stats)     || {};
        }
      } catch(e) { console.error(e); }
      this.loading = false;
    },
    async loadLookups() {
      try {
        var res = await Promise.all([
          executeJson({ function: "getBranches" }, hms),
          executeJson({ function: "getPatients" }, hms)
        ]);
        if (res[0].data && res[0].data.success) this.branches = (res[0].data.data && res[0].data.data.Branches) || [];
        if (res[1].data && res[1].data.success) this.patients = (res[1].data.data && res[1].data.data.Patients) || [];
      } catch(e) {}
    },

    async openDetail(r) {
      try {
        var resp = await executeJson({ function: "getReferralDetail", referral_id: r.referral_id }, hms);
        if (resp.data && resp.data.success) this.detail = resp.data.data;
        else this.showToast(resp.data.message || "Failed", true);
      } catch(e) { this.showToast("Connection error", true); }
    },

    async acceptCurrent() {
      this.processing = true;
      try {
        var r = await executeJson({ function: "acceptReferral", referral_id: this.detail.Referral.referral_id }, hms);
        if (r.data && r.data.success) { this.showToast("Referral accepted"); this.detail = null; await this.loadAll(); }
        else this.showToast(r.data.message || "Failed", true);
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    },
    openReject() { this.rejectReason = ''; this.rejectError = ''; this.showReject = true; },
    async rejectCurrent() {
      this.rejectError = '';
      if (!this.rejectReason) { this.rejectError = 'Reason is required.'; return; }
      this.processing = true;
      try {
        var r = await executeJson({ function: "rejectReferral", referral_id: this.detail.Referral.referral_id, rejection_reason: this.rejectReason }, hms);
        if (r.data && r.data.success) {
          this.showToast("Referral rejected"); this.showReject = false; this.detail = null; await this.loadAll();
        } else this.rejectError = r.data.message || "Failed";
      } catch(e) { this.rejectError = "Connection error"; }
      this.processing = false;
    },
    async completeCurrent() {
      this.processing = true;
      try {
        var r = await executeJson({ function: "completeReferral", referral_id: this.detail.Referral.referral_id }, hms);
        if (r.data && r.data.success) { this.showToast("Referral marked complete"); this.detail = null; await this.loadAll(); }
        else this.showToast(r.data.message || "Failed", true);
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    },
    async cancelCurrent() {
      if (!confirm("Cancel this referral? The receiving facility will be notified.")) return;
      this.processing = true;
      try {
        var r = await executeJson({ function: "cancelReferral", referral_id: this.detail.Referral.referral_id }, hms);
        if (r.data && r.data.success) { this.showToast("Cancelled"); this.detail = null; await this.loadAll(); }
        else this.showToast(r.data.message || "Failed", true);
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    },

    openChart() {
      if (!this.detail) return;
      this.$router.push('/patients/' + this.detail.Referral.patient_id + '/chart');
    },

    openCreate() {
      this.newRef = { patient_id:'', to_branch_id:'', reason:'', clinical_summary:'', priority:'routine', transport_arranged:0, transport_details:'' };
      this.createError = '';
      this.showCreate = true;
    },
    async submitCreate() {
      this.createError = '';
      var f = this.newRef;
      if (!f.patient_id || !f.to_branch_id || !f.reason) { this.createError = 'Patient, destination, and reason are required.'; return; }
      this.processing = true;
      try {
        var r = await executeJson(Object.assign({ function: "createReferral" }, f), hms);
        if (r.data && r.data.success) {
          this.showToast("Referral sent");
          this.showCreate = false;
          this.direction = 'outbound';
          await this.loadAll();
        } else this.createError = r.data.message || "Failed";
      } catch(e) { this.createError = "Connection error"; }
      this.processing = false;
    },

    ageStr(dob) { if (!dob||dob==='0000-00-00'||dob==='1900-01-01') return '?'; var d=new Date(dob); if (isNaN(d)) return '—'; return Math.floor((Date.now()-d.getTime())/(365.25*24*3600000))+'y'; },
    truncate(s,n) { return (s||'').length>n ? s.substring(0,n)+'...' : (s||''); },
    formatDate(s) { if (!s) return '—'; var d=new Date(s.replace?s.replace(' ','T'):s); return isNaN(d)?s:d.toLocaleDateString(); },
    formatDateTime(s) { if (!s) return '—'; var d=new Date(s.replace?s.replace(' ','T'):s); return isNaN(d)?s:d.toLocaleString(); },
    relativeTime(s) {
      if (!s) return ''; var d=new Date(s.replace?s.replace(' ','T'):s); if (isNaN(d)) return s;
      var diff = (Date.now() - d.getTime()) / 1000;
      if (diff < 60)    return Math.floor(diff)+'s ago';
      if (diff < 3600)  return Math.floor(diff/60)+'m ago';
      if (diff < 86400) return Math.floor(diff/3600)+'h ago';
      if (diff < 604800)return Math.floor(diff/86400)+'d ago';
      return d.toLocaleDateString();
    },
    priorityBadge(p) { return ({ emergency:'hms-badge-danger', urgent:'hms-badge-warning', routine:'hms-badge-info' })[p] || 'hms-badge-neutral'; },
    statusBadge(s) { return ({ pending:'hms-badge-warning', accepted:'hms-badge-success', rejected:'hms-badge-danger', completed:'hms-badge-purple', cancelled:'hms-badge-neutral' })[s] || 'hms-badge-neutral'; },
    news2Class(s) { if (s == null) return ''; if (s >= 7) return 'news2-high'; if (s >= 5) return 'news2-med'; return 'news2-low'; }
  }
};
</script>
